import styled from "@emotion/styled";
import type { FC } from "react";
import { default as React } from "react";
import { COACHING_RATING_QUESTIONNAIRE_ID } from "src/constants/misc";
import translate from "src/lib/translate";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import Track from "src/ui/components/Track/Track";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import CustomQuestionnaire from "../../CustomQuestionnaire/CustomQuestionnaire";
import { CenterQuestionnaireContent } from "../../InAppQuestionnaire/InAppQuestionnaire";
import { useSearchParams } from "react-router-dom";
import RateCoachingCallCubit from "src/state/RateCoachingCallCubit/RateCoachingCallCubit";
import { useBlocNext } from "@blac/react";
import Loader from "../../Loader/Loader";
import { TaskResponse } from "@9amhealth/openapi";
import { HandHighFiveIcon } from "src/constants/icons";
import Translate from "src/ui/components/Translate/Translate";
import { Button } from "@9amhealth/shared";
import Path from "src/constants/path";

const Wrapper = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: 8.25rem 1rem;
  text-align: center;
  gap: 1rem;
`;

const WidthConstraint = styled.div`
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
`;

const ErrorState: FC<{ onClose: () => void }> = ({ onClose }) => (
  <Wrapper>
    <h4>
      <Translate msg="error.generic.short" />
    </h4>
    <p style={{ textAlign: "center" }}>
      <Translate msg="error.generic.short.tryAgain" />
    </p>
    <Button onClick={onClose} theme="sunrise">
      <Translate msg="returnToHome" />
    </Button>
  </Wrapper>
);

const CoachRatingDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [queryParams] = useSearchParams();
  const appointmentId = queryParams.get("appointmentId") ?? "";

  const [
    { isLoading, task, hiddenFields },
    { loadRateCoachingCallTaskForAppointment }
  ] = useBlocNext(RateCoachingCallCubit, {
    onMount: () => {
      if (appointmentId) {
        void loadRateCoachingCallTaskForAppointment(appointmentId);
      }
    }
  });

  const onClose = (navigateToHome?: boolean) => {
    AppQueryPopupsController.closePopup();
    if (navigateToHome) {
      navigate(Path.home);
    } else {
      navigate(returnUrl, {
        multiBack: true,
        replace: true
      });
    }
  };

  const onComplete = () => {
    onClose();
  };

  const { isCompleted, showErrorScreen } = React.useMemo(() => {
    const completed = task?.status === TaskResponse.status.COMPLETED;
    const available = task?.status === TaskResponse.status.AVAILABLE;
    const showError = !task || (!available && !completed);

    return {
      isCompleted: completed,
      showErrorScreen: showError
    };
  }, [task]);

  return (
    <InDialog
      title={translate("yourFeedback")}
      onClose={onClose}
      returnUrl={returnUrl}
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
    >
      {isLoading ? (
        <Loader />
      ) : showErrorScreen ? (
        <ErrorState onClose={() => onClose(true)} />
      ) : isCompleted ? (
        <Wrapper>
          <HandHighFiveIcon aria-hidden="true" />
          <h4>
            <Translate msg="feedbackAlreadyReceived.title" />
          </h4>
          <p style={{ textAlign: "center" }}>
            <Translate msg="feedbackAlreadyReceived.description" />
          </p>
          <Button onClick={() => onClose(true)} theme="sunrise">
            <Translate msg="returnToHome" />
          </Button>
        </Wrapper>
      ) : (
        <CenterQuestionnaireContent>
          <Track
            event={TrackEvent.CoachingRatingDialog}
            type={TrackType.open}
          />
          <WidthConstraint>
            <CustomQuestionnaire
              id={COACHING_RATING_QUESTIONNAIRE_ID}
              onLastStepCompleted={onComplete}
              preventLoadingUserAnswers
              hiddenFields={hiddenFields}
            />
          </WidthConstraint>
        </CenterQuestionnaireContent>
      )}
    </InDialog>
  );
};

export default CoachRatingDialog;
